import React, { useState, useEffect } from 'react';
import { 
  Monitor, 
  Package, 
  Settings, 
  Presentation,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

const IMSDashboard = ({ axiosInstance }) => {
  const [stats, setStats] = useState({
    machines: 0,
    consumables: 0,
    spare_parts: 0,
    demo_units: 0
  });

  // Recent Activity State
  const [recentActivity, setRecentActivity] = useState([]);
  const [slowMovingItems, setSlowMovingItems] = useState([]);

  useEffect(() => {
    if (axiosInstance) {
      fetchStats();
      fetchDashboardData();
    }
  }, [axiosInstance]);

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get('/ims/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching IMS dashboard stats:", error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Fetch Stock-In and Stock-Out records
      const [stockInRes, stockOutRes] = await Promise.all([
        axiosInstance.get('/stock/in'),
        axiosInstance.get('/stock/out')
      ]);

      const stockInRecords = stockInRes.data || [];
      const stockOutRecords = stockOutRes.data || [];

      processRecentActivity(stockInRecords, stockOutRecords);
      processSlowMovingItems(stockInRecords, stockOutRecords);

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  const processRecentActivity = (stockIn, stockOut) => {
    // 1. Normalize Data
    const activityList = [];

    // Process Stock-In
    stockIn.forEach(record => {
      // Stock-In records might have an 'items' array or be single items depending on structure
      // Based on Records.js, it seems stock-in might be grouped or single. 
      // Let's assume record structure similar to what we see in Records.js or API response.
      // If record has 'items', iterate. If it's a direct item, use it.
      // Looking at Records.js, it groups records. Let's look at raw data structure assumption.
      // Usually stock/in returns list of transactions.
      
      const items = record.items || [record]; // Handle both grouped and flat structures if any
      items.forEach(item => {
        activityList.push({
          movement: 'Stock-In',
          warehouse: item.warehouse || 'Main', // Default if missing
          brand: item.brand || '-',
          model: item.model || '-',
          description: item.itemDescription || item.description || '-',
          code: item.itemCode || item.partNumber || '-',
          quantity: item.quantity || item.orderQuantity || 0,
          date: new Date(record.date || record.createdAt || Date.now()),
          si_number: record.si_number || '-',
          dr_number: record.dr_number || '-',
          supplier: record.supplier || '-',
          delivery_date: record.delivery_date ? new Date(record.delivery_date).toLocaleDateString() : '-'
        });
      });
    });

    // Process Stock-Out
    stockOut.forEach(record => {
      const items = record.items || [];
      items.forEach(item => {
        activityList.push({
          movement: 'Stock-Out',
          warehouse: item.warehouse || 'Main', // Default if missing
          brand: item.brand || '-',
          model: item.model || '-',
          description: item.itemDescription || item.description || '-',
          code: item.itemCode || item.partNumber || '-',
          quantity: item.quantity || item.orderQuantity || 0,
          date: new Date(record.orderDate || record.date || Date.now())
        });
      });
    });

    // 2. Filter Last 2 Weeks
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);

    const filteredActivity = activityList.filter(item => item.date >= twoWeeksAgo);

    // 3. Sort Descending
    filteredActivity.sort((a, b) => b.date - a.date);

    setRecentActivity(filteredActivity);
  };

  const processSlowMovingItems = (stockIn, stockOut) => {
    // Logic: Items present in Stock-In but haven't moved (Stock-Out) in > 1 month.
    // 1. Identify all unique items (using Code or Description as key) from Stock-In
    // 2. Find last Stock-Out date for each item
    // 3. Filter items where last Stock-Out is older than 1 month OR never stocked out.

    const itemMap = {};

    // Build Inventory Map from Stock-In (Proxy for "Items we have")
    // We only care about the LATEST Stock-In date to know it's recent inventory? 
    // No, we care about existing items.
    stockIn.forEach(record => {
      const items = record.items || [record];
      items.forEach(item => {
        const key = item.itemCode || item.partNumber || item.itemDescription; // Use Code or Description as unique ID
        if (!key) return;

        if (!itemMap[key]) {
          itemMap[key] = {
            code: key,
            description: item.itemDescription || item.description || '-',
            brand: item.brand || '-',
            model: item.model || '-',
            lastStockOut: null,
            totalStockIn: 0
          };
        }
        itemMap[key].totalStockIn += (item.quantity || 0);
      });
    });

    // Check Stock-Outs to find last movement
    stockOut.forEach(record => {
      const date = new Date(record.orderDate || record.date);
      const items = record.items || [];
      items.forEach(item => {
        const key = item.itemCode || item.partNumber || item.itemDescription;
        if (itemMap[key]) {
          if (!itemMap[key].lastStockOut || date > itemMap[key].lastStockOut) {
            itemMap[key].lastStockOut = date;
          }
        }
      });
    });

    // Filter Slow Moving (> 1 Month)
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    const slowMoving = Object.values(itemMap).filter(item => {
      // If never stocked out, it's slow moving (if it was stocked in before 1 month ago?)
      // Let's stick to simple: No Stock-Out in last 1 month.
      if (!item.lastStockOut) return true; // Never moved
      return item.lastStockOut < oneMonthAgo; // Moved more than a month ago
    });

    // Limit to top 20 or so for dashboard display
    setSlowMovingItems(slowMoving.slice(0, 20));
  };

  const summaryTiles = [
    {
      title: "Machines",
      value: stats.machines,
      icon: Monitor,
      color: "bg-red-500", // Red (was Total customer)
      iconColor: "text-white"
    },
    {
      title: "Consumables",
      value: stats.consumables,
      icon: Package,
      color: "bg-green-600", // Green (was Total Suppliers)
      iconColor: "text-white"
    },
    {
      title: "Spare Parts",
      value: stats.spare_parts,
      icon: Settings,
      color: "bg-cyan-600", // Teal/Cyan (was Total sells)
      iconColor: "text-white"
    },
    {
      title: "Demo Unit",
      value: stats.demo_units,
      icon: Presentation,
      color: "bg-slate-500", // Grey (was Total purchase)
      iconColor: "text-white"
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Top Row: Summary Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {summaryTiles.map((tile, index) => (
          <Card key={index} className={`${tile.color} border-none shadow-md`}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-white">
                {tile.title}
              </CardTitle>
              <div className="h-10 w-10 rounded-full border-2 border-white/20 flex items-center justify-center">
                <tile.icon className={`h-5 w-5 ${tile.iconColor}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{tile.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Bottom Row: Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="text-base">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Movement</TableHead>
                    <TableHead>Warehouse</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead>Model</TableHead>
                    <TableHead>Item Description</TableHead>
                    <TableHead>Item Code / Part #</TableHead>
                    <TableHead>SI No.</TableHead>
                    <TableHead>DR No.</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Delivery Date</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentActivity.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="h-24 text-center text-muted-foreground">
                        No recent activity (Last 2 weeks)
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentActivity.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${item.movement === 'Stock-in' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {item.movement}
                          </span>
                        </TableCell>
                        <TableCell>{item.warehouse}</TableCell>
                        <TableCell>{item.brand}</TableCell>
                        <TableCell>{item.model}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>{item.code}</TableCell>
                        <TableCell>{item.si_number || '-'}</TableCell>
                        <TableCell>{item.dr_number || '-'}</TableCell>
                        <TableCell>{item.supplier || '-'}</TableCell>
                        <TableCell>{item.delivery_date || '-'}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Slow Moving Inventories */}
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="text-base">Slow Moving Inventories (No movement &gt; 1 month)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">#</TableHead>
                    <TableHead>Item Code / Part #</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Total Stock-In</TableHead>
                    <TableHead className="text-right">Last Stock-Out</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {slowMovingItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                        No slow moving items found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    slowMovingItems.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.code}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className="text-right">{item.totalStockIn}</TableCell>
                        <TableCell className="text-right">
                          {item.lastStockOut ? new Date(item.lastStockOut).toLocaleDateString() : 'Never'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IMSDashboard;
